import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, Zap, Clock, CheckCircle, Star, Building2 } from 'lucide-react';
import { formatCurrencySimple } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PASystemCard = ({ system }) => {
  const navigate = useNavigate();

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${BACKEND_URL}${url}`;
  };

  return (
    <Card className="overflow-hidden hover:shadow-2xl transition-all duration-300 group border-gray-700 bg-gray-800 hover:border-yellow-500">
      <div className="relative overflow-hidden h-56">
        <img
          src={getImageUrl(system?.image)}
          alt={system?.name || 'PA System'}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-4 right-4">
          <Badge className="bg-yellow-500 text-black font-semibold">
            {system?.category || 'PA System'}
          </Badge>
        </div>
        {system?.available && (
          <div className="absolute top-4 left-4">
            <Badge className="bg-green-500 text-white">
              <CheckCircle className="h-3 w-3 mr-1" />
              Available
            </Badge>
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-transparent to-transparent" />
      </div>
      <CardHeader className="pb-2">
        <CardTitle className="text-xl text-white">{system?.name || 'Unnamed System'}</CardTitle>
        <p className="text-gray-400 text-sm mt-2 line-clamp-2">{system?.description || ''}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Business Info */}
        {system?.business && (
          <div className="flex items-center gap-2 text-sm text-gray-400 pb-2 border-b border-gray-700">
            <Building2 className="h-4 w-4 text-yellow-500" />
            <span>{system.business.business_name}</span>
            {system.business.average_rating && (
              <div className="flex items-center gap-1 ml-auto">
                <Star className="h-3 w-3 text-yellow-500 fill-yellow-500" />
                <span>{system.business.average_rating}</span>
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-gray-400">
            <Users className="h-4 w-4 mr-2 text-yellow-500" />
            {system?.capacity || 'N/A'}
          </div>
          <div className="flex items-center text-gray-400">
            <Zap className="h-4 w-4 mr-2 text-yellow-500" />
            {system?.specs?.power || 'N/A'}
          </div>
        </div>
        <div className="flex items-center text-sm text-gray-400">
          <Clock className="h-4 w-4 mr-2 text-yellow-500" />
          Setup: {system?.specs?.setup || 'N/A'}
        </div>
        <div className="pt-3 border-t border-gray-700">
          <div className="flex items-baseline">
            <span className="text-2xl font-bold text-yellow-500">{formatCurrencySimple(system?.price || 0)}</span>
            <span className="text-gray-500 ml-2">/{system?.price_unit || 'per day'}</span>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full bg-yellow-500 hover:bg-yellow-600 text-black font-semibold transition-all"
          onClick={() => navigate(`/system/${system?.system_id}`)}
        >
          View Details & Book
        </Button>
      </CardFooter>
    </Card>
  );
};

export default PASystemCard;
